import { useKeycloak } from "@react-keycloak/web";
import { AdminApiClient } from "./admin/AdminApiClient";
import ConfigInterface from "../ConfigInterface";
import { PartnerApiClient } from "./partner/PartnerApiClient";
import { useMemo } from "react";
import { CareApiClient } from "./care/CareApiClient";

export function useApiClient(config: ConfigInterface) {
  const { keycloak } = useKeycloak();
  return useMemo(() => {
    const getToken = async () => {
      await keycloak.updateToken(60);
      return keycloak.token!;
    };
    return {
      adminClient: new AdminApiClient(new HttpClient(config.adminApiUrl, getToken)),
      partnerClient: new PartnerApiClient(new HttpClient(config.partnerApiUrl, getToken)),
      careClient: new CareApiClient(new HttpClient(config.careApiUrl, getToken)),
    };
  }, [config]);
}

export interface HttpClientInterface {
  get<T>(url: string): Promise<T>;
  getFile(url: string): Promise<string>;
  post<T>(url: string, body: object): Promise<T>;
  postFile<T>(url: string, body: File, key: string, responseType: "text" | "json" | "none"): Promise<T>;
  put<T>(url: string, body: object): Promise<T>;
  patch<T>(url: string, body: object): Promise<T>;
  delete<T>(url: string): Promise<T>;
}
export class HttpClient implements HttpClientInterface {
  constructor(
    private baseUrl: string,
    private token: () => Promise<string>,
  ) { }
  async get<T>(url: string): Promise<T> {
    return await fetch(this.makeUrl(url), {
      method: "GET",
      headers: await this.getHeaders(),
    }).then((res) => res.json());
  }
  async getFile(url: string): Promise<string> {
    return await fetch(this.makeUrl(url), {
      method: "GET",
      headers: await this.getHeaders(),
    }).then((res) => res.text());
  }
  async post<T>(url: string, body: object): Promise<T> {
    return await fetch(this.makeUrl(url), {
      method: "POST",
      headers: await this.getHeaders("application/json"),
      body: JSON.stringify(body),
    }).then((res) => res.json());
  }

  async postFile<T>(url: string, body: File, key: string, responseType: "text" | "json" | "none"): Promise<T> {
    const data = new FormData();
    data.set(key, body);
    const response = await fetch(this.makeUrl(url), {
      method: "POST",
      headers: await this.getHeaders(),
      body: data,
    });
    if (responseType === "none") {
      return null as T;
    }
    if (responseType === "text") {
      return (await response.text()) as T;
    }
    return response.json();
  }

  async put<T>(url: string, body: object): Promise<T> {
    return await fetch(this.makeUrl(url), {
      method: "PUT",
      headers: await this.getHeaders("application/json"),
      body: JSON.stringify(body),
    }).then((res) => res.json());
  }

  async patch<T>(url: string, body: object): Promise<T> {
    return await fetch(this.makeUrl(url), {
      method: "PATCH",
      headers: await this.getHeaders("application/json"),
      body: JSON.stringify(body),
    }).then((res) => res.json());
  }

  async delete<T>(url: string): Promise<T> {
    return await fetch(this.makeUrl(url), {
      method: "DELETE",
      headers: await this.getHeaders(),
    }).then((res) => res.json());
  }

  private makeUrl(url: string): string {
    let baseUrl = this.baseUrl;
    if (baseUrl.endsWith("/")) {
      baseUrl = baseUrl.slice(0, -1);
    }
    if (!url.startsWith("/")) {
      url = "/" + url;
    }
    return baseUrl + url;
  }
  private async getHeaders(contentType?: string) {
    const baseHeaders = {
      Accept: "application/json",
      ...(contentType ? { "Content-Type": contentType } : {}),
    };
    const token = await this.token();
    if (token) {
      return {
        ...baseHeaders,
        Authorization: `Bearer ${token}`,
      };
    }
    return baseHeaders;
  }
}
